import React, { useEffect, useState } from 'react';
import { Button, Space, Tag, Spin } from 'antd';
import { RightOutlined, CloseOutlined, PartitionOutlined } from '@ant-design/icons';
import AttributeForm from './AttributeForm';
import { attrsForType } from './attrsForType';
import { typeStyle } from './componentTypeStyle';
import { getHallazgosByComponentApi } from 'api/hallazgoComponent';
import ComponentHealthPanel from './ComponentHealthPanel';
import './componentTree.css';

const SEV_COLOR = { BAJA: 'green', MEDIA: 'blue', ALTA: 'orange', CRITICA: 'red' };

// Renderiza un hallazgo individual (estilo compartido por las tres secciones).
function HallazgoItem({ h }) {
  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: '8px 10px',
        marginBottom: 8,
      }}
    >
      <Space size={6} wrap style={{ marginBottom: 4 }}>
        <Tag color={h.tipo === 'INCIDENCIA' ? 'volcano' : 'gold'}>{h.tipo}</Tag>
        {h.severidad ? <Tag color={SEV_COLOR[h.severidad]}>{h.severidad}</Tag> : null}
        <Tag color={h.estado === 'INACTIVA' ? 'default' : 'green'}>
          {h.estado === 'INACTIVA' ? 'Cerrado' : 'Activo'}
        </Tag>
        {h.provisional && (
          <Tag color="purple" style={{ fontSize: 10 }}>
            provisional
          </Tag>
        )}
      </Space>
      <div style={{ fontSize: 13 }}>{h.descripcion}</div>
    </div>
  );
}

// Título de sub-sección de hallazgos.
function HallazgoSection({ label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          color: '#9ca3af',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {items.map((h) => (
        <HallazgoItem key={h.id} h={h} />
      ))}
    </div>
  );
}

// Lista (solo lectura) de hallazgos vinculados a un nodo de componente,
// clasificados en Abiertos / Provisionales / Resueltos.
function LinkedHallazgos({ equipoId, nodeId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!equipoId || !nodeId) {
      setItems([]);
      return undefined;
    }
    setItems(null);
    getHallazgosByComponentApi(equipoId, nodeId)
      .then((d) => {
        if (alive) setItems(d?.hallazgos || []);
      })
      .catch(() => {
        if (alive) setItems([]);
      });
    return () => {
      alive = false;
    };
  }, [equipoId, nodeId]);

  // Clasificación:
  // Abiertos   → ACTIVA y NO provisional
  // Provisionales → provisional === true (independiente del estado)
  // Resueltos  → INACTIVA (y no provisional — los provisionales resueltos van en Provisionales)
  const abiertos = items?.filter((h) => h.estado === 'ACTIVA' && !h.provisional) ?? [];
  const provisionales = items?.filter((h) => h.provisional) ?? [];
  const resueltos = items?.filter((h) => h.estado !== 'ACTIVA' && !h.provisional) ?? [];

  const hasAny = abiertos.length + provisionales.length + resueltos.length > 0;

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color: '#6b7280',
          marginBottom: 8,
        }}
      >
        Hallazgos vinculados
      </div>
      {items === null ? (
        <Spin size="small" />
      ) : !hasAny ? (
        <div style={{ fontSize: 12, color: '#9ca3af' }}>
          Sin hallazgos vinculados a este componente.
        </div>
      ) : (
        <>
          <HallazgoSection label="Abiertos" items={abiertos} />
          <HallazgoSection label="Provisionales" items={provisionales} />
          <HallazgoSection label="Resueltos" items={resueltos} />
        </>
      )}
    </div>
  );
}

function ComponentInspector({
  node,
  path = [],
  defs = [],
  values = {},
  canEdit = false,
  imageApi = null,
  onChange,
  onSave,
  onClose,
  floating = false,
  showClose = true,
  equipoId = null,
  health = null,
  onHealthChanged,
}) {
  if (!node) {
    return (
      <div className={'cmp-inspector' + (floating ? ' floating' : '')}>
        <div
          className="cmp-insp-body"
          style={{ textAlign: 'center', color: '#9ca3af', paddingTop: 40 }}
        >
          <PartitionOutlined style={{ fontSize: 26 }} />
          <div style={{ marginTop: 10, fontWeight: 600, color: '#6b7280' }}>
            Seleccioná un componente
          </div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            Hacé clic en un nodo del árbol o del mapa para ver y editar sus atributos.
          </div>
        </div>
      </div>
    );
  }

  const t = typeStyle(node.raw?.node_type ?? null);
  const Icon = t.Icon;
  const filteredDefs = attrsForType(defs, node.raw?.node_type ?? null);

  return (
    <div className={'cmp-inspector' + (floating ? ' floating' : '')}>
      <div className="cmp-crumb" style={{ justifyContent: 'space-between' }}>
        <Space size={6} wrap>
          <span
            className="cmp-tic"
            style={{ background: t.tint, color: t.color, width: 22, height: 22 }}
          >
            <Icon style={{ fontSize: 12 }} />
          </span>
          {path.map((p, i) => (
            <React.Fragment key={p.id}>
              {i > 0 && <RightOutlined style={{ fontSize: 9, color: '#bcc4cd' }} />}
              <span className={i === path.length - 1 ? 'cur' : ''}>{p.name}</span>
            </React.Fragment>
          ))}
        </Space>
        {showClose && (
          <Button size="small" type="text" icon={<CloseOutlined />} onClick={onClose} />
        )}
      </div>

      <div className="cmp-insp-body">
        {/* Panel de salud: badge + ack/override + historial */}
        {equipoId && (
          <ComponentHealthPanel
            equipoId={equipoId}
            nodeId={node.key}
            health={health}
            onChanged={onHealthChanged}
          />
        )}

        <AttributeForm
          defs={filteredDefs}
          values={values}
          onChange={canEdit ? onChange : undefined}
          readOnly={!canEdit}
          imageApi={imageApi}
        />
        {equipoId ? <LinkedHallazgos equipoId={equipoId} nodeId={node.key} /> : null}
      </div>

      {canEdit && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            padding: 12,
            borderTop: '1px solid #e5e7eb',
          }}
        >
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="primary" onClick={onSave}>
            Guardar
          </Button>
        </div>
      )}
    </div>
  );
}

export default ComponentInspector;
